import React, { useEffect } from 'react';
import { Plus, Minus, Info, X, Trash2, HelpCircle } from 'lucide-react';
import type { Unit } from '@/modelos/Unidad';
import type { ProductFormData } from '@/modelos/productos/Producto';
import type { AdditionalUnitError } from '@/hooks/productos/useProductoFormulario';
import { resolveUnitLabelText } from './SeccionUnidades';
import Tooltip from '@/contasis/components/Tooltip/Tooltip';
import { TextButton } from '@/contasis/components/TextButton';
import { Input } from '@/contasis/components/Input';
import { Select } from '@/contasis/components/Select';

interface ProductAdditionalUnitsTableProps {
  unidadesMedidaAdicionales: ProductFormData['unidadesMedidaAdicionales'];
  baseUnitCode: ProductFormData['unidad'];
  additionalUnitErrors: AdditionalUnitError[];
  addAdditionalUnit: () => void;
  removeAdditionalUnit: (index: number) => void;
  updateAdditionalUnit: (index: number, field: 'nombre' | 'unidadCodigo' | 'factorConversion', value: string) => void;
  getAdditionalUnitOptions: (rowIndex: number) => Unit[];
  remainingUnitsForAdditional: Unit[];
  findUnitByCode: (code?: string) => Unit | undefined;
  formatFactorValue: (value?: number) => string;
  unitInfoMessage: string | null;
  setUnitInfoMessage: (message: string | null) => void;
  showCheck?: boolean;
  renderCheck?: (className?: string) => React.ReactNode;
}

export const ProductAdditionalUnitsTable: React.FC<ProductAdditionalUnitsTableProps> = ({
  unidadesMedidaAdicionales,
  baseUnitCode,
  additionalUnitErrors,
  addAdditionalUnit,
  removeAdditionalUnit,
  updateAdditionalUnit,
  getAdditionalUnitOptions,
  remainingUnitsForAdditional,
  findUnitByCode,
  formatFactorValue,
  unitInfoMessage,
  setUnitInfoMessage,
  showCheck,
  renderCheck
}) => {
  useEffect(() => {
    if (!unitInfoMessage) return;
    const timer = setTimeout(() => setUnitInfoMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [unitInfoMessage, setUnitInfoMessage]);

  return (
    <div className="mt-4 space-y-2">
      <div className="flex items-center gap-2">
          <span className="text-h6 font-display text-secondary whitespace-nowrap">Presentaciones comerciales</span>
          <Tooltip text="Crea equivalencias: caja = 12 unidades, pack = 6, etc." position="right">
            <button
              id="unidades-adicionales-ayuda"
              name="unidadesAdicionalesAyuda"
              type="button"
              aria-label="Ayuda: Presentaciones comerciales"
              className="inline-flex items-center justify-center rounded-sm text-tertiary hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </Tooltip>
          {showCheck && renderCheck?.('ml-2')}
          <div className="flex-1 h-px bg-border-default" />
          <TextButton
          id="unidades-adicionales-agregar"
          name="unidadesAdicionalesAgregar"
          intent="primary"
          size="sm"
          type="button"
          onClick={addAdditionalUnit}
          disabled={remainingUnitsForAdditional.length === 0}
          iconLeft={<Plus size={12} />}
        >
          Agregar presentación
        </TextButton>
      </div>

      {unitInfoMessage && (
        <div className="flex items-start gap-2 rounded-md border border-[var(--warning-border)] bg-warning-bg px-2.5 py-2 text-label text-secondary">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-warning" />
          <span className="flex-1">{unitInfoMessage}</span>
          <button
            id="unidades-adicionales-mensaje-cerrar"
            name="unidadesAdicionalesMensajeCerrar"
            type="button"
            onClick={() => setUnitInfoMessage(null)}
            className="text-warning hover:text-[var(--warning-hover)]"
            aria-label="Ocultar mensaje"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {unidadesMedidaAdicionales.length > 0 && (
        <div className="rounded-lg bg-surface-2 border border-border-default p-2 space-y-1">
          {/* Encabezados de columna */}
          <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1.1fr)_130px_minmax(0,1.2fr)_32px] gap-x-3 px-3 pb-0.5">
            <span className="text-label text-tertiary">Nombre de presentación</span>
            <span className="text-label text-tertiary">Unidad</span>
            <div className="flex items-center gap-1">
              <span className="text-label text-tertiary">Contiene</span>
              <Tooltip text="Cantidad de unidades mínimas que incluye esta presentación." position="right">
                <button
                  id="unidades-adicionales-contiene-ayuda"
                  name="unidadesAdicionalesContieneAyuda"
                  type="button"
                  aria-label="Ayuda: Contiene"
                  className="inline-flex items-center justify-center rounded-sm text-tertiary hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                >
                  <HelpCircle className="w-3 h-3" />
                </button>
              </Tooltip>
            </div>
            <span className="text-label text-tertiary">Equivale a</span>
            <div />
          </div>
          {/* Filas */}
          {unidadesMedidaAdicionales.map((unit, index) => {
            const options = getAdditionalUnitOptions(index);
            const baseUnit = findUnitByCode(baseUnitCode);
            const baseUnitLabel = baseUnit ? resolveUnitLabelText(baseUnit) : baseUnitCode || '—';
            const selectedUnit = findUnitByCode(unit.unidadCodigo);
            const selectedUnitLabel = selectedUnit ? resolveUnitLabelText(selectedUnit) : unit.unidadCodigo;
            const conversionText =
              unit.unidadCodigo && unit.factorConversion
                ? `1 ${selectedUnitLabel} = ${formatFactorValue(unit.factorConversion)} ${baseUnitLabel}`
                : null;

            return (
              <div key={index} className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1.1fr)_130px_minmax(0,1.2fr)_32px] gap-x-3 px-3 py-2 items-center bg-surface-0 rounded-md border border-border-default">
                <Input
                  id={`unidades-adicionales-nombre-${index + 1}`}
                  name={`unidadesAdicionalesNombre${index + 1}`}
                  size="small"
                  value={unit.nombre ?? ''}
                  onChange={(e) => updateAdditionalUnit(index, 'nombre', e.target.value)}
                  placeholder="Ej: Caja, Pack, Bolsa"
                />
                <Select
                  id={`unidades-adicionales-unidad-${index + 1}`}
                  name={`unidadesAdicionalesUnidad${index + 1}`}
                  size="small"
                  value={unit.unidadCodigo}
                  onChange={(e) => updateAdditionalUnit(index, 'unidadCodigo', e.target.value)}
                  placeholder="Seleccionar"
                  options={options.map(o => ({ value: o.code, label: resolveUnitLabelText(o) }))}
                  error={additionalUnitErrors[index]?.unidad}
                />
                <div className={`flex items-center rounded-md border h-8 overflow-hidden ${additionalUnitErrors[index]?.factor ? 'border-error' : 'border-border-default'}`}>
                  <button
                    id={`unidades-adicionales-factor-disminuir-${index + 1}`}
                    name={`unidadesAdicionalesFactorDisminuir${index + 1}`}
                    type="button"
                    onClick={() => {
                      const current = parseFloat(String(unit.factorConversion)) || 1;
                      updateAdditionalUnit(index, 'factorConversion', String(Math.max(1, current - 1)));
                    }}
                    className="px-2 h-full flex items-center text-tertiary hover:bg-surface-hover border-r border-border-default"
                  >
                    <Minus size={11} />
                  </button>
                  <input
                    id={`unidades-adicionales-factor-${index + 1}`}
                    name={`unidadesAdicionalesFactor${index + 1}`}
                    type="number"
                    value={unit.factorConversion ?? ''}
                    onChange={(e) => updateAdditionalUnit(index, 'factorConversion', e.target.value)}
                    className="flex-1 min-w-0 text-center text-caption text-primary bg-transparent outline-none"
                    min={1}
                    step={1}
                  />
                  <button
                    id={`unidades-adicionales-factor-aumentar-${index + 1}`}
                    name={`unidadesAdicionalesFactorAumentar${index + 1}`}
                    type="button"
                    onClick={() => {
                      const current = parseFloat(String(unit.factorConversion)) || 0;
                      updateAdditionalUnit(index, 'factorConversion', String(current + 1));
                    }}
                    className="px-2 h-full flex items-center text-tertiary hover:bg-surface-hover border-l border-border-default"
                  >
                    <Plus size={11} />
                  </button>
                </div>
                <span className="text-caption text-secondary truncate">
                  {conversionText ?? <span className="text-tertiary">—</span>}
                </span>
                <Tooltip text="Eliminar presentación" position="left">
                  <button
                    id={`unidades-adicionales-eliminar-${index + 1}`}
                    name={`unidadesAdicionalesEliminar${index + 1}`}
                    type="button"
                    onClick={() => removeAdditionalUnit(index)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 text-muted hover:bg-error-bg hover:text-error"
                    aria-label="Eliminar presentación"
                  >
                    <Trash2 size={16} />
                  </button>
                </Tooltip>
              </div>
            );
          })}
        </div>
      )}

      {remainingUnitsForAdditional.length === 0 && unidadesMedidaAdicionales.length > 0 && (
        <p className="text-caption text-tertiary">Ya agregaste todas las unidades disponibles para esta familia.</p>
      )}
    </div>
  );
};
